import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Card from '../Card';
import InputField from '../../../../components/Form/InputField';
import { colors } from '../../../../colors';
import LoadingIcons from 'react-loading-icons';
import { Validations } from '../../../../components/Form';
import { useCreateUserMutation } from '../../../../store/services/api';

interface Props {
  isActive: boolean;
  moveToStepTwo: (userId: number) => void;
  handleBackPress: () => void;
}

const UserOnboarding = forwardRef(
  (
    { isActive, moveToStepTwo, handleBackPress }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [createUser] = useCreateUserMutation();

    const [email, setEmail] = useState<string | undefined>();
    const [username, setUsername] = useState();
    const [password, setPassword] = useState<string | undefined>();
    const [passwordRepeat, setPasswordRepeat] = useState<string | undefined>();

    const [invalidEmailError, setInvalidEmailError] = useState(false);
    const [isPasswordOutOfSync, setIsPasswordOutOfSync] = useState(false);
    const [isUserCreateLoading, setIsUserCreateLoading] = useState(false);
    const [userCreateError, setUserCreateError] = useState<
      string | undefined
    >();

    const handleEmailChange = useCallback((email: string) => {
      setEmail(email);

      if (email.includes('@')) {
        setInvalidEmailError(false);
      }
    }, []);

    const handlePasswordChange = useCallback(
      (password: string) => {
        setPassword(password);

        if (password !== passwordRepeat) {
          setIsPasswordOutOfSync(false);
        }
      },
      [passwordRepeat],
    );

    const handlePasswordRepeatChange = useCallback(
      (password: string) => {
        setPasswordRepeat(password);

        if (password !== passwordRepeat) {
          setIsPasswordOutOfSync(false);
        }
      },
      [passwordRepeat],
    );

    const emailField = useMemo(() => {
      return {
        label: 'Email',
        value: email,
        handleValueChange: handleEmailChange,
        shouldFit: true,
        error: invalidEmailError ? Validations.InvalidEmail : undefined,
      };
    }, [email, handleEmailChange, invalidEmailError]);

    const usernameField = useMemo(() => {
      return {
        label: 'Username',
        value: username,
        handleValueChange: setUsername,
        shouldFit: true,
      };
    }, [username]);

    const passwordField = useMemo(() => {
      return {
        label: 'Password',
        value: password,
        handleValueChange: handlePasswordChange,
        shouldFit: true,
        isSecret: true,
      };
    }, [password, handlePasswordChange]);

    const passwordRepeatField = useMemo(() => {
      return {
        label: 'Repeat Password',
        value: passwordRepeat,
        handleValueChange: handlePasswordRepeatChange,
        shouldFit: true,
        isSecret: true,
        error: isPasswordOutOfSync ? Validations.OutOfSyncPassword : undefined,
      };
    }, [handlePasswordRepeatChange, isPasswordOutOfSync, passwordRepeat]);

    const handleCreateUser = useCallback(async () => {
      if (!email || !username || !password || !passwordRepeat) {
        return;
      }

      if (!email.includes('@')) {
        setInvalidEmailError(true);
        return;
      }

      if (password !== passwordRepeat) {
        setIsPasswordOutOfSync(true);
        return;
      }

      setIsUserCreateLoading(true);

      const result = await createUser({ email, username, password });

      if (result.data?.error) {
        setUserCreateError(result.data?.error);
      } else if (result.data?.success) {
        // Move to step two
        moveToStepTwo(result.data.userId);
      }

      setIsUserCreateLoading(false);
    }, [createUser, email, moveToStepTwo, password, passwordRepeat, username]);

    const disableButton = !email || !username || !password || !passwordRepeat;

    return (
      <Card
        index={1}
        title="User Onboarding"
        isActive={isActive}
        ref={ref}
        handleBackPress={handleBackPress}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <InputField config={emailField} />
          <InputField config={usernameField} />
          <InputField config={passwordField} />
          <InputField config={passwordRepeatField} />
          {userCreateError && (
            <p
              style={{
                color: colors.red,
                margin: 0,
                marginBottom: 0,
                marginTop: -8,
              }}
            >
              {userCreateError}
            </p>
          )}
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'end' }}
          >
            <button
              onClick={handleCreateUser}
              style={{
                backgroundColor: disableButton ? colors.gray : colors.green,
                border: `1px solid ${
                  disableButton ? colors.gray : colors.green
                }`,
                borderRadius: 4,
                cursor: disableButton ? 'default' : 'pointer',
                padding: 6,
                paddingLeft: 12,
                paddingRight: 12,
                color: colors.white,
                fontWeight: 'bold',
                textWrap: 'nowrap',
                fontSize: 16,
              }}
              disabled={disableButton}
            >
              {isUserCreateLoading ? (
                <LoadingIcons.Grid width={15} height={15} />
              ) : (
                'Create User'
              )}
            </button>
          </div>
        </div>
      </Card>
    );
  },
);

UserOnboarding.displayName = 'UserOnboarding';

export default UserOnboarding;

/** Ask for username, email and password */
